import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DropdownMC from '../../../../components/DropdownMC';
import IconMC from '../../../../components/IconMC';
import TooltipMC from '../../../../components/TooltipMC';
import { CATALOG_PRODUCT_LIMIT } from '../Agents/constants/tools';
import { fetchActiveToolTypes } from '../Agents/hooks/activeToolsCache';
import { isFlowToolAvailable, V2_FLOW_TOOLS } from './conversationFlowTools';
import { fetchGuideSignals } from './guideSignalsCache';

const EMPTY_GUIDE_SIGNALS = {
  hasQaGuides: false,
  hasSpecificInstructions: false,
  truncated: false
};

const ConversationFlowToolsSelector = ({ agentId, onSelectTool }) => {
  const { t } = useTranslation('ChatbotIA');
  const [availability, setAvailability] = useState(null);
  const [pickerKey, setPickerKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    if (!agentId) {
      setAvailability({
        activeToolTypes: [],
        ...EMPTY_GUIDE_SIGNALS,
        missingAgent: true
      });
      return undefined;
    }

    setAvailability(null);

    Promise.all([
      fetchActiveToolTypes(agentId),
      fetchGuideSignals().catch(() => EMPTY_GUIDE_SIGNALS)
    ]).then(([types, guideSignals]) => {
      if (cancelled) return;

      setAvailability({
        activeToolTypes: types || [],
        hasQaGuides: Boolean(guideSignals?.hasQaGuides),
        hasSpecificInstructions: Boolean(guideSignals?.hasSpecificInstructions),
        truncated: Boolean(guideSignals?.truncated),
        missingAgent: false
      });
    });

    return () => {
      cancelled = true;
    };
  }, [agentId]);

  const options = useMemo(() => {
    if (!availability || availability.missingAgent) return [];

    return V2_FLOW_TOOLS
      .filter((tool) => isFlowToolAvailable(tool, availability))
      .map((tool) => ({
        id: tool.id,
        label: t(tool.titleKey, { limit: CATALOG_PRODUCT_LIMIT }),
        description: t(tool.descriptionKey, { limit: CATALOG_PRODUCT_LIMIT }),
        icon: tool.icon,
        bgClass: tool.bgClass,
        colorClass: tool.colorClass,
        isBeta: tool.isBeta
      }));
  }, [availability, t]);

  const handleSelect = (toolId) => {
    const tool = options.find((option) => option.id === toolId);
    if (!tool) return;

    onSelectTool(tool);
    setPickerKey((current) => current + 1);
  };

  const showEmpty = Boolean(availability) && options.length === 0;

  return (
    <div className="tw-mt-3">
      <span className="tw-block tw-text-zinc-700 tw-mb-1 tw-text-sm tw-font-bold">
        {t('config.conversation_flow.tools_label')}
      </span>
      <p className="tw-mb-2 tw-mt-0 tw-font-regular tw-text-xs tw-text-gray-950">
        {t('config.conversation_flow.tools_description')}
      </p>
      {availability?.truncated && (
        <p className="tw-mb-2 tw-text-xs tw-text-yellow-700">
          {t('config.conversation_flow.tools_partial')}
        </p>
      )}
      {showEmpty ? (
        <p className="tw-mb-0 tw-text-xs tw-text-gray-500">
          {t('config.conversation_flow.tools_empty')}
        </p>
      ) : (
        <DropdownMC
          key={pickerKey}
          id="conversation-flow-tools"
          placeholder={t('config.conversation_flow.tools_placeholder')}
          options={options}
          disabled={!availability}
          onSelect={handleSelect}
          renderOption={(option) => (
            <TooltipMC
              text={option.description}
              position="left"
              portal
              className="tw-w-full"
            >
              <span className="tw-flex tw-items-center tw-gap-2 tw-w-full">
                <span className={`tw-p-1 tw-rounded-md tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-shrink-0 ${option.bgClass}`}>
                  <IconMC name={option.icon} size="sm" className={option.colorClass} />
                </span>
                <span className="tw-text-sm tw-font-medium tw-text-gray-950">{option.label}</span>
                {option.isBeta && (
                  <span className="tw-px-1.5 tw-py-0.5 tw-bg-purple-50 tw-text-purple-500 tw-text-[10px] tw-rounded">
                    {t('tools.beta_badge')}
                  </span>
                )}
              </span>
            </TooltipMC>
          )}
        />
      )}
    </div>
  );
};

export default ConversationFlowToolsSelector;
